import React, { useEffect } from 'react'
import { PageTitle } from '../../uiComponents/typography'
import { SigmaService, EmbedUrlParams } from './sigmaService'
import { usePageHeight, useSigmaIframe } from '@sigmacomputing/react-embed-sdk'
import { getPreferredLanguage, LanguageSelector } from 'uiComponents/languageSelector/languageSelector'
import { useAppSelector } from 'store/hooks'
import { getPartnerCountryCode } from 'auth/selectors'
import { usePermission } from 'admin/hocs'
import PermissionRequired from 'admin/permissionRequired'
import { useLocation } from 'react-router-dom'
import { parseHashRoute } from 'utils/hash'
import './sigmaReport.scss'

interface SigmaReportProps {
    accountSlug: string
    sigmaService: SigmaService
    reportId: string
    title: string
}

export const SigmaReport = ({ accountSlug, sigmaService, reportId, title }: SigmaReportProps) => {
    const { iframeRef } = useSigmaIframe()
    const height = Math.ceil(usePageHeight(iframeRef) || 0)
    const [embedUrl, setEmbedUrl] = React.useState('')
    const { hasPermission } = usePermission()
    const hasAnalyticsPermission = hasPermission('access_analytics', accountSlug)
    const countryCode = useAppSelector(getPartnerCountryCode)
    // Sigma page/element nodeId deep link, e.g. /reports/sales#uywwmBHTxq.
    // Read from the router location so hash-only navigation (palette deep
    // links between report pages) re-renders and refetches the embed. The
    // hash is kept in the URL so refresh and history reopen the same page.
    const location = useLocation()
    const nodeId = parseHashRoute(location.hash)

    const getUrlParams = (): EmbedUrlParams => ({
        ...(nodeId ? { nodeId } : {}),
        responsive_height: true,
        menu_position: 'top',
        lng: countryCode === 'FR' ? getPreferredLanguage() : countryCode === 'US' ? 'en' : 'en-gb',
    })

    // Sequence requests: rapid hash navigation fires overlapping fetches, and
    // an out-of-order response must not overwrite the newest embed.
    const requestIdRef = React.useRef(0)
    const fetchEmbedUrl = (urlParams: EmbedUrlParams) => {
        const requestId = ++requestIdRef.current
        sigmaService.getEmbedUrl(accountSlug, reportId, urlParams).then((url) => {
            if (requestId === requestIdRef.current) {
                setEmbedUrl(url)
            }
        })
    }

    useEffect(() => {
        if (!embedUrl && hasAnalyticsPermission) {
            fetchEmbedUrl(getUrlParams())
        }
    })

    useEffect(() => {
        setEmbedUrl('')
    }, [hasAnalyticsPermission, accountSlug, reportId, countryCode, nodeId])

    return (
        <>
            <PageTitle>{title}</PageTitle>
            <PermissionRequired name="access_analytics" accountSlug={accountSlug}>
                {countryCode === 'FR' && (
                    <LanguageSelector
                        options={[
                            { name: 'English', value: 'en-gb' },
                            { name: 'French', value: 'fr' },
                        ]}
                        onChange={() => {
                            fetchEmbedUrl(getUrlParams())
                        }}
                    />
                )}
                {embedUrl && <iframe className="sigma-iframe" ref={iframeRef} style={{ height }} src={embedUrl} />}
            </PermissionRequired>
        </>
    )
}
